import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { Badge } from "@/components/ui/shadcn/Badge/Badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/shadcn/HoverCard/HoverCard";
import { Typography } from "@/components/ui/Typography/Typography";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { searchMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import { type MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import { cn } from "@/lib/utils/cn";
import { formatContactsName, formatDate } from "@/lib/utils/format";
import { getMatterTagChipClassNames } from "@/lib/utils/matters/matter-tag-color";
import { useQuery } from "@tanstack/react-query";
import { type ReactElement, useMemo } from "react";

export type MatterPreviewData = {
  matterId?: string;
  matterTitle?: string | null;
  matterAssignee?: string[] | null;
  matterClientName?: string | null;
  matterDateOfLoss?: string | null;
  matterDefendantName?: string[] | null;
  matterIncidentAddressCity?: string | null;
  matterIncidentAddressState?: string | null;
  label?: string | null;
  category?: string | null;
  matterTag?: string[] | null;
  matterTeam?: string[] | null;
  incidentType?: string | null;
  claimTypes?: Array<{ id: string; label: string }>;
};

const normalizeMatterPreviewValues = (
  values: string[] | null | undefined,
): string[] => {
  const seen = new Set<string>();

  return (values ?? []).filter((value): value is string => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false;
    }

    const normalizedValue = trimmedValue.toLowerCase();
    if (seen.has(normalizedValue)) {
      return false;
    }

    seen.add(normalizedValue);
    return true;
  });
};

const summarizeMatterPreviewValues = (
  values: string[] | null | undefined,
  maxItems = 2,
) => {
  const normalizedValues = normalizeMatterPreviewValues(values);

  if (normalizedValues.length === 0) {
    return undefined;
  }

  const visibleValues = normalizedValues.slice(0, maxItems);
  const remainingCount = normalizedValues.length - visibleValues.length;

  return remainingCount > 0
    ? `${visibleValues.join(", ")} +${remainingCount}`
    : visibleValues.join(", ");
};

const getMatterPreviewLocation = (matter: MatterPreviewData) => {
  const location = [
    matter.matterIncidentAddressCity?.trim(),
    matter.matterIncidentAddressState?.trim(),
  ]
    .filter((value): value is string => Boolean(value))
    .join(", ");

  return location || undefined;
};

const MatterHoverPreviewField = ({
  label,
  value,
}: {
  label: string;
  value?: string;
}) => {
  if (!value) {
    return null;
  }

  return (
    <div className="space-y-1">
      <Typography className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </Typography>
      <Typography className="text-sm leading-5">{value}</Typography>
    </div>
  );
};

const MatterHoverPreviewChipSection = ({
  chipClassName,
  label,
  values,
}: {
  chipClassName?: (value: string) => string | undefined;
  label: string;
  values: string[] | null | undefined;
}) => {
  const normalizedValues = normalizeMatterPreviewValues(values);

  if (normalizedValues.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      <Typography className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </Typography>
      <div className="flex flex-wrap gap-1.5">
        {normalizedValues.slice(0, 3).map((value) => (
          <Badge
            key={value}
            variant="outline"
            className={cn("text-[11px]", chipClassName?.(value))}
          >
            {value}
          </Badge>
        ))}
        {normalizedValues.length > 3 ? (
          <Badge variant="outline" className="text-[11px]">
            +{normalizedValues.length - 3}
          </Badge>
        ) : null}
      </div>
    </div>
  );
};

const mapSearchResultToMatterPreview = (
  matter: MatterSearchResult | undefined,
): MatterPreviewData | undefined => {
  if (!matter) {
    return undefined;
  }

  return {
    matterId: matter.id,
    matterAssignee: matter.assignees
      ?.map((assignee) =>
        `${assignee.user.firstName} ${assignee.user.lastName}`.trim(),
      )
      .filter(Boolean),
    matterClientName:
      getMatterContactConnections(matter)
        .filter((connection) => connection.label === "Client")
        .map((connection) => formatContactsName(connection.contact))
        .find(Boolean) ?? null,
    matterDateOfLoss: matter.dateOfLoss,
    matterDefendantName: getMatterContactConnections(matter)
      .filter((connection) => connection.label === "Defendant")
      .map((connection) => formatContactsName(connection.contact))
      .filter((name): name is string => Boolean(name)),
    matterIncidentAddressCity: matter.incidentAddressCity,
    matterIncidentAddressState: matter.incidentAddressState,
    label: matter.status?.label,
    matterTag: [],
    matterTeam: [],
    matterTitle: matter.title,
    incidentType: matter.incidentType?.label,
    claimTypes: matter.claimTypes ?? [],
  };
};

const hasDetailedMatterPreview = (previewData: MatterPreviewData | undefined) =>
  Boolean(
    previewData?.matterClientName ||
      previewData?.matterDateOfLoss ||
      previewData?.label ||
      previewData?.incidentType ||
      previewData?.matterIncidentAddressCity ||
      previewData?.matterIncidentAddressState ||
      normalizeMatterPreviewValues(previewData?.matterAssignee).length ||
      normalizeMatterPreviewValues(previewData?.matterDefendantName).length ||
      normalizeMatterPreviewValues(previewData?.matterTag).length ||
      normalizeMatterPreviewValues(previewData?.matterTeam).length,
  );

const mergeMatterPreviewData = (
  base: MatterPreviewData | undefined,
  override: MatterPreviewData | undefined,
) => {
  if (!base) {
    return override;
  }

  if (!override) {
    return base;
  }

  const definedOverrides: MatterPreviewData = {};

  (Object.keys(override) as (keyof MatterPreviewData)[]).forEach((key) => {
    const value = override[key];
    if (value !== undefined) {
      (definedOverrides as Record<string, unknown>)[key] = value;
    }
  });

  return { ...base, ...definedOverrides };
};

export const MatterPreviewHoverCard = ({
  align = "start",
  children,
  matterId,
  previewData,
  side = "bottom",
}: {
  align?: "center" | "start" | "end";
  children: ReactElement;
  matterId?: string;
  previewData?: MatterPreviewData;
  side?: "top" | "right" | "bottom" | "left";
}) => {
  const shouldFetchPreview =
    Boolean(matterId) && !hasDetailedMatterPreview(previewData);
  const { data: mattersResponse } = useQuery({
    ...searchMattersQueryOptions({
      matterIds: matterId ? [matterId] : [],
      pageSize: 1,
    }),
    enabled: shouldFetchPreview,
  });
  const normalizedMatterTags = normalizeMatterPreviewValues(
    previewData?.matterTag,
  );
  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: normalizedMatterTags.length > 0 || shouldFetchPreview,
  });

  const matterPreview = useMemo(() => {
    const matchingMatter = mattersResponse?.data.find(
      (matter) => matter.id === matterId,
    );
    return mergeMatterPreviewData(
      mapSearchResultToMatterPreview(matchingMatter),
      previewData,
    );
  }, [matterId, mattersResponse?.data, previewData]);
  const tagColorClassByLabel = useMemo(() => {
    const tagColorMap = new Map<string, string>();

    (matterSettingsData?.data.tags ?? []).forEach((tag) => {
      const normalizedLabel = tag.label.trim().toLowerCase();
      if (!normalizedLabel) {
        return;
      }

      tagColorMap.set(normalizedLabel, getMatterTagChipClassNames(tag.color));
    });

    return tagColorMap;
  }, [matterSettingsData?.data.tags]);

  const title = matterPreview?.matterTitle?.trim() || "Untitled matter";
  const clientName = matterPreview?.matterClientName?.trim() || undefined;
  const defendantName = summarizeMatterPreviewValues(
    matterPreview?.matterDefendantName,
  );
  const assigneeLabel = summarizeMatterPreviewValues(
    matterPreview?.matterAssignee,
  );
  const location = getMatterPreviewLocation(matterPreview ?? {});
  const formattedDateOfLoss = matterPreview?.matterDateOfLoss
    ? formatDate(matterPreview.matterDateOfLoss)
    : undefined;
  const hasDetails = Boolean(
    clientName ||
      matterPreview?.label ||
      matterPreview?.incidentType ||
      defendantName ||
      assigneeLabel ||
      formattedDateOfLoss ||
      location ||
      normalizeMatterPreviewValues(matterPreview?.matterTeam).length ||
      normalizeMatterPreviewValues(matterPreview?.matterTag).length,
  );

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        className="w-80 rounded-xl p-4"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <Typography className="text-foreground text-sm leading-5 font-semibold">
                  {title}
                </Typography>
                {clientName ? (
                  <Typography className="text-muted-foreground text-sm leading-5">
                    {clientName}
                  </Typography>
                ) : null}
              </div>
              {matterPreview?.label ? (
                <Badge variant="secondary" className="shrink-0">
                  {matterPreview.label}
                </Badge>
              ) : null}
            </div>
            {matterPreview?.incidentType ? (
              <Badge variant="outline" className="text-[11px]">
                {matterPreview.incidentType}
              </Badge>
            ) : null}
            {matterPreview?.claimTypes?.map((claim) => (
              <Badge key={claim.id} variant="outline" className="text-[11px]">
                {claim.label}
              </Badge>
            ))}
          </div>

          {hasDetails ? (
            <div className="space-y-3">
              <MatterHoverPreviewField
                label="Defendant"
                value={defendantName}
              />
              <MatterHoverPreviewField label="Assignee" value={assigneeLabel} />
              <MatterHoverPreviewField
                label="Date of loss"
                value={formattedDateOfLoss}
              />
              <MatterHoverPreviewField label="Location" value={location} />
              <MatterHoverPreviewChipSection
                label="Teams"
                values={matterPreview?.matterTeam}
              />
              <MatterHoverPreviewChipSection
                chipClassName={(value) =>
                  tagColorClassByLabel.get(value.trim().toLowerCase())
                }
                label="Tags"
                values={matterPreview?.matterTag}
              />
            </div>
          ) : (
            <Typography className="text-muted-foreground text-sm">
              No additional matter details available.
            </Typography>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
